import * as React from "react";
import Table from "@amzn/awsui-components-react/polaris/table";
import Box from "@amzn/awsui-components-react/polaris/box";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Button from "@amzn/awsui-components-react/polaris/button";
import Header from "@amzn/awsui-components-react/polaris/header";
import Container from "@amzn/awsui-components-react/polaris/container";
import { useDispatch, useSelector } from "react-redux";
import {
  DateType,
  MilestoneDate,
  Schedule,
} from "../../server/milestone/model";
import { getCountryCode } from "../../from-server/acs/acs-service";
import { isProdStage } from "../../util/stageUtils";
import { RootState } from "../app/rootReducer";
import { useEffect } from "react";
import { currentPagePorAsinsBelongToSameProgram } from "../common/PorAsinListUtils";
import { setProgram } from "./milestoneScheduleTableConnector";
import { fetchMilestoneSchedules } from "./milestoneScheduleTableConnector";
import { FullAsinWithAudits } from "../../server/asin-central/model";

interface PivotedMilestoneDate {
  date?: string;
  countries: string[];
  descriptor?: string;
}

interface Milestone {
  dateType: DateType;
  announceDates: PivotedMilestoneDate[];
  streetDates: PivotedMilestoneDate[];
}

const convertMarketplacesToCountry = (
  milestones: MilestoneDate[]
): string[] => {
  return milestones
    .map((m) => m.marketplace)
    .filter((m) => m)
    .map((m) => getCountryCode(m!))
    .sort() as string[];
};

const pivotMilestoneDates = (
  dates: MilestoneDate[]
): PivotedMilestoneDate[] => {
  const datesMap = dates.reduce((map, date) => {
    // bucket dates by date value, whether it has marketplaces and descriptor
    const dateKey = `${date.date}-${Boolean(date.marketplace)}-${
      date.descriptor
    }`;
    if (map.has(dateKey)) {
      map.get(dateKey)!.push(date);
    } else {
      map.set(dateKey, [date]);
    }
    return map;
  }, new Map<string, MilestoneDate[]>());

  return Array.from(datesMap.values(), (dates) => ({
    date: dates[0].date,
    countries: convertMarketplacesToCountry(dates),
    descriptor: dates[0].descriptor,
  })).sort((a, b) => a.date?.localeCompare(b.date) || 1); // if date is null (i.e. comment), put it at the end
};

const filterMilestoneDates = (
  type: DateType,
  dates: MilestoneDate[]
): Milestone => {
  return {
    dateType: type,
    announceDates: pivotMilestoneDates(
      dates.filter((d) => d.dateType === type && d.milestoneName === "Announce")
    ),
    streetDates: pivotMilestoneDates(
      dates.filter((d) => d.dateType === type && d.milestoneName === "Street")
    ),
  };
};

const aggregateMilestoneDates = (schedule: Schedule): Milestone[] => {
  return [
    filterMilestoneDates("POR", schedule.milestoneDates || []),
    filterMilestoneDates("LE", schedule.milestoneDates || []),
  ];
};

const PivotedMilestoneCell = (milestone: PivotedMilestoneDate) => {
  return (
    <Box>
      <Box>
        <SpaceBetween direction="horizontal" size="xs">
          <Box fontWeight="bold" display="inline">
            {milestone.date || ""}
          </Box>
          <Box display="inline">{milestone.countries.join(",")}</Box>
        </SpaceBetween>
      </Box>
      <Box color="text-body-secondary">{milestone.descriptor || ""}</Box>
    </Box>
  );
};

const MilestoneCell = (pivotedMilestone: PivotedMilestoneDate[]) => {
  return <Box>{pivotedMilestone.map((m) => PivotedMilestoneCell(m))}</Box>;
};

const getScheduleLink = (schedule: Schedule): string => {
  return `https://milestones${
    isProdStage() ? "" : ".beta"
  }.starforge.devices.a2z.com/#/program/${schedule.programId}`;
};

const getUniqueSchedule = (schedules: Schedule[]): Schedule | undefined => {
  if (schedules.length === 1) return schedules[0];
};

const getEmptyStateMessage = (
  schedules: Schedule[],
  programId: string,
  programName: string
) => {
  return (
    <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
      {schedules.length === 0 ? (
        <b>
          No schedule is associated to program: "{programName}" in Milestone
          Manager
        </b>
      ) : (
        <div>
          <b>
            Found multiple schedules associated to program: "{programName}" in
            Milestone Manager
          </b>
          <p>
            Please clean up the program & schedule association to have dates
            populated here
          </p>
          {schedules.map((s) => (
            <a
              href={getScheduleLink(s)}
              target="_blank"
              rel="noreferrer"
              key={s.programId}
            >
              <p>{s.programName}</p>
            </a>
          ))}
        </div>
      )}
    </Box>
  );
};

export const MilestoneScheduleTable = () => {
  const dispatch = useDispatch();

  const { currentPagePorAsins, isLoading } = useSelector(
    (state: RootState) => state.porAsinList
  );
  const { program, loading, schedules } = useSelector(
    (state: RootState) => state.milestoneScheduleState
  );

  useEffect(() => {
    if (!isLoading) {
      // if ASINs are from the same program
      if (
        currentPagePorAsins.length > 0 &&
        currentPagePorAsinsBelongToSameProgram(currentPagePorAsins)
      ) {
        const asin = currentPagePorAsins[0] as FullAsinWithAudits;
        const p = {
          programId: asin.programId,
          programName: asin.programName,
        };
        // only fetch the schedule if program has changed
        if (p !== program) {
          dispatch(fetchMilestoneSchedules(p));
        }
        // if no ASIN OR if ASINs are from different programs AND program has been set before, clear the state
      } else if (program) {
        dispatch(setProgram());
      }
    } else if (program) {
      dispatch(setProgram());
    }
  }, [currentPagePorAsins, isLoading]);

  const schedule = getUniqueSchedule(schedules);

  const header = "Program Schedule";
  return program?.programId ? (
    <Table
      columnDefinitions={[
        {
          id: "dateType",
          header: "",
          cell: (item) => item.dateType,
          sortingField: "dateType",
          isRowHeader: true,
        },
        {
          id: "announceDate",
          header: "Announce Date",
          cell: (item) => MilestoneCell(item.announceDates),
        },
        {
          id: "streetDate",
          header: "Street Date",
          cell: (item) => MilestoneCell(item.streetDates),
        },
      ]}
      items={schedule ? aggregateMilestoneDates(schedule) : []}
      loading={loading}
      loadingText="Loading"
      sortingDisabled
      trackBy={(item) => item.dateType}
      empty={getEmptyStateMessage(
        schedules,
        program!.programId,
        program!.programName
      )}
      header={
        <Header
          description={`Milestone Manager dates for program: "${
            program!.programName
          }"`}
          actions={
            schedule ? (
              <Button
                href={getScheduleLink(schedule)}
                iconAlign="right"
                iconName="external"
                target="_blank"
              >
                View Schedule in Milestone Manager
              </Button>
            ) : (
              ""
            )
          }
        >
          {header}
        </Header>
      }
    />
  ) : (
    <Container header={<Header>{header}</Header>}>
      <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
        <b>
          To see Milestone Manager schedules, please refine your search criteria
          to only include ASINs from a single (unrestricted) program
        </b>
      </Box>
    </Container>
  );
};
